import type { SupabaseClient } from '@supabase/supabase-js';

export type ContactTier = 'A' | 'B' | 'C' | 'D' | 'Unassigned';
export type ContactStatus = 'Not Contacted' | 'Called' | 'Follow-up' | 'Meeting Scheduled' | 'Closed';

export interface ContactCore {
    id: string;
    first_name: string;
    last_name: string;
    title?: string | null;
    company?: string | null;
    email?: string | null;
    phone?: string | null;
    email_1_value?: string | null;
    phone_1_value?: string | null;
    profile_image_url?: string | null;
    photo?: string | null;
    notes?: string | null;
    tier?: ContactTier | null;
    status?: ContactStatus | null;
    last_contacted?: string | null;
    is_priority?: boolean;
    is_archived: boolean;
    latitude?: number | null;
    longitude?: number | null;
    created_at?: string;
    updated_at?: string;
}

export async function listContactsCore(
    sb: SupabaseClient,
    opts: { query?: string; tier?: ContactTier | ''; limit?: number; offset?: number; includeArchived?: boolean } = {}
): Promise<ContactCore[]> {
    const { query, tier, limit = 50, offset = 0, includeArchived = false } = opts;
    let s = sb.from('contacts').select('*');
    if (!includeArchived) s = s.eq('is_archived', false);
    const q = (query || '').trim();
    if (q) {
        s = s.or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%,company.ilike.%${q}%,email.ilike.%${q}%,email_1_value.ilike.%${q}%`);
    }
    if (tier) s = s.eq('tier', tier);
    const { data, error } = await s.order('created_at', { ascending: false }).range(offset, offset + limit - 1);
    if (error) throw error;
    return (data || []) as ContactCore[];
}

export async function getContactCore(sb: SupabaseClient, id: string): Promise<ContactCore | null> {
    const { data, error } = await sb.from('contacts').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return (data as ContactCore) || null;
}

export type CreateContactInput = Partial<Omit<ContactCore, 'id' | 'is_archived'>> & { first_name?: string; last_name?: string };
export async function createContactCore(sb: SupabaseClient, input: CreateContactInput): Promise<ContactCore> {
    const now = new Date().toISOString();
    const row: any = {
        ...input,
        is_archived: false,
        created_at: now,
        updated_at: now,
    };
    const { data, error } = await sb.from('contacts').insert(row).select('*').maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('Insert returned no data');
    return data as ContactCore;
}

export type UpdateContactInput = Partial<Omit<ContactCore, 'id'>>;
export async function updateContactCore(sb: SupabaseClient, id: string, patch: UpdateContactInput): Promise<ContactCore> {
    const row = { ...patch, updated_at: new Date().toISOString() } as any;
    const { data, error } = await sb.from('contacts').update(row).eq('id', id).select('*').maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('Update returned no data');
    return data as ContactCore;
}

export async function toggleArchiveContactCore(sb: SupabaseClient, id: string, toArchived: boolean): Promise<ContactCore> {
    const { data, error } = await sb
        .from('contacts')
        .update({ is_archived: toArchived, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('*')
        .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('Archive toggle returned no data');
    return data as ContactCore;
}

// Group-scoped contacts
export async function listContactsForGroupCore(
    sb: SupabaseClient,
    groupId: string,
    opts: { query?: string; tier?: ContactTier | ''; limit?: number; offset?: number; includeArchived?: boolean } = {}
): Promise<ContactCore[]> {
    if (!groupId) throw new Error('listContactsForGroupCore: groupId is required');
    const { query, tier, limit = 50, offset = 0, includeArchived = false } = opts;
    let s = sb
        .from('contacts')
        .select('*, contact_groups:contact_groups!inner(group_id)')
        .eq('contact_groups.group_id', groupId);
    if (!includeArchived) s = s.eq('is_archived', false);
    const q = (query || '').trim();
    if (q) {
        s = s.or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%,company.ilike.%${q}%,email.ilike.%${q}%,email_1_value.ilike.%${q}%`);
    }
    if (tier) s = s.eq('tier', tier);
    const { data, error } = await s.order('created_at', { ascending: false }).range(offset, offset + limit - 1);
    if (error) throw error;
    // Strip the join alias before returning
    return ((data || []) as any[]).map((row) => {
        const { contact_groups: _cg, ...rest } = row || {};
        return rest as ContactCore;
    });
}

export async function addContactToGroupCore(sb: SupabaseClient, contactId: string, groupId: string): Promise<void> {
    if (!contactId || !groupId) throw new Error('addContactToGroupCore: contactId and groupId are required');
    const { error } = await sb.from('contact_groups').insert({ contact_id: contactId, group_id: groupId });
    if (error) throw error;
}

export async function removeContactFromGroupCore(sb: SupabaseClient, contactId: string, groupId: string): Promise<void> {
    if (!contactId || !groupId) throw new Error('removeContactFromGroupCore: contactId and groupId are required');
    const { error } = await sb.from('contact_groups').delete().match({ contact_id: contactId, group_id: groupId });
    if (error) throw error;
}

export async function listContactGroupIdsCore(sb: SupabaseClient, contactId: string): Promise<string[]> {
    if (!contactId) throw new Error('listContactGroupIdsCore: contactId is required');
    const { data, error } = await sb
        .from('contact_groups')
        .select('group_id')
        .eq('contact_id', contactId);
    if (error) throw error;
    return (data || []).map((r: any) => r.group_id).filter(Boolean);
}

export async function deleteContactCore(sb: SupabaseClient, contactId: string): Promise<void> {
    if (!contactId) throw new Error('deleteContactCore: contactId is required');
    const { error } = await sb.from('contacts').delete().eq('id', contactId);
    if (error) throw error;
}

export async function deleteContactsCore(sb: SupabaseClient, contactIds: string[]): Promise<void> {
    if (!Array.isArray(contactIds) || contactIds.length === 0) {
        throw new Error('deleteContactsCore: contactIds must be a non-empty array');
    }
    const { error } = await sb.from('contacts').delete().in('id', contactIds);
    if (error) throw error;
}

export interface ContactCountsCore {
    total: number;
    tierA: number;
    tierB: number;
    tierC: number;
    tierD: number;
    followUpNeeded: number;
    priorityContacts: number;
}

export async function getContactCountsForUserCore(sb: SupabaseClient, userId: string): Promise<ContactCountsCore> {
    if (!userId) throw new Error('getContactCountsForUserCore: userId is required');
    const totalRes = await sb.from('contacts').select('*', { count: 'exact', head: true }).eq('user_id', userId);
    if (totalRes.error) throw totalRes.error;
    const { data, error } = await sb.from('contacts').select('tier, status, is_priority').eq('user_id', userId);
    if (error) throw error;
    const arr = (data || []) as any[];
    const tierA = arr.filter(c => c.tier === 'A').length;
    const tierB = arr.filter(c => c.tier === 'B').length;
    const tierC = arr.filter(c => c.tier === 'C').length;
    const tierD = arr.filter(c => c.tier === 'D').length;
    const followUp = arr.filter(c => c.status === 'Follow-up').length;
    const priority = arr.filter(c => c.is_priority).length;
    return { total: totalRes.count || 0, tierA, tierB, tierC, tierD, followUpNeeded: followUp, priorityContacts: priority };
}

export async function getContactCountsForGroupCore(sb: SupabaseClient, groupId: string): Promise<ContactCountsCore> {
    if (!groupId) throw new Error('getContactCountsForGroupCore: groupId is required');
    // total count via head query with join
    const totalRes = await sb
        .from('contacts')
        .select('id, contact_groups:contact_groups!inner(group_id)', { count: 'exact', head: true })
        .eq('contact_groups.group_id', groupId);
    if (totalRes.error) throw totalRes.error;
    const { data, error } = await sb
        .from('contacts')
        .select('tier, status, is_priority, contact_groups:contact_groups!inner(group_id)')
        .eq('contact_groups.group_id', groupId);
    if (error) throw error;
    const arr = ((data || []) as any[]).map((r: any) => {
        const { contact_groups: _cg, ...rest } = r || {}; return rest;
    });
    const tierA = arr.filter((c: any) => c.tier === 'A').length;
    const tierB = arr.filter((c: any) => c.tier === 'B').length;
    const tierC = arr.filter((c: any) => c.tier === 'C').length;
    const tierD = arr.filter((c: any) => c.tier === 'D').length;
    const followUp = arr.filter((c: any) => c.status === 'Follow-up').length;
    const priority = arr.filter((c: any) => c.is_priority).length;
    return { total: totalRes.count || 0, tierA, tierB, tierC, tierD, followUpNeeded: followUp, priorityContacts: priority };
}

export async function updateContactCoordinatesCore(sb: SupabaseClient, id: string, latitude: number, longitude: number): Promise<void> {
    if (!id) throw new Error('updateContactCoordinatesCore: id is required');
    if (typeof latitude !== 'number' || typeof longitude !== 'number') throw new Error('updateContactCoordinatesCore: latitude/longitude must be numbers');
    const { error } = await sb.from('contacts').update({ latitude, longitude, updated_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
}
